import { useState } from "react";
import { Edit2, Info, Loader2, Save, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useKeepDescription, useUpdateKbDescription, useWriteDescription } from "@/hooks/use-knowledge-base";
import { failureMessage, type KnowledgeBase, type KnowledgeBaseFile } from "@/services/knowledge-base.service";
import { cleanDescription, descriptionCount, descriptionError, hasLongFile } from "@/utils/kb-description-rules";

/** Over a description written from files longer than what it read, and after a rewrite of one (design §5). */
const FIRST_CHARACTERS =
  "Only the first 5,000 characters of each file were used to write this description. Check it, and edit it by hand if your files are longer.";

// Neutral: a description from before step 1 may be counted as hand-written without being one.
const REVIEW = "Your files have changed since this description was saved. Update the description from your files?";

/** Why Generate, Regenerate and Update are off: the function refuses them too. */
const NO_READY_FILE = "No file is ready yet, so there is nothing to write the description from.";

const TRY_AGAIN = "Try again in a moment.";

interface DescriptionPanelProps {
  kb: KnowledgeBase;
  files: KnowledgeBaseFile[];
  workspaceId: string;
  canEdit: boolean;
}

/**
 * The tool description the agent reads to decide when to search this knowledge base: written from the
 * files, or by hand (checked as the function checks it). A hand-written one is never rewritten by an
 * upload; once the files change, the page asks whether to.
 */
export function DescriptionPanel({ kb, files, workspaceId, canEdit }: DescriptionPanelProps) {
  const { toast } = useToast();
  const save = useUpdateKbDescription(workspaceId);
  const keep = useKeepDescription(workspaceId);
  const write = useWriteDescription(workspaceId);
  /** The text being edited; null when not editing. */
  const [draft, setDraft] = useState<string | null>(null);
  const problem = draft === null ? null : descriptionError(draft);
  const longFile = hasLongFile(files);
  const noReadyFile = !files.some((file) => file.status === "ready");
  const writing = write.isPending || keep.isPending;

  const failed = (title: string) => (error: unknown) =>
    toast({ variant: "destructive", title, description: failureMessage(error, TRY_AGAIN) });

  const rewrite = () =>
    write.mutate(kb.id, {
      onSuccess: () =>
        toast({ title: "Description updated", description: longFile ? FIRST_CHARACTERS : "It was written from your files." }),
      onError: failed("The description was not updated"),
    });

  // Save is disabled while the draft breaks a rule, so what is sent is always accepted.
  const saveDraft = () =>
    save.mutate(
      { storeId: kb.id, description: cleanDescription(draft) || null },
      {
        onSuccess: () => {
          setDraft(null);
          toast({ title: "Description saved", description: "Uploads no longer rewrite it. You will be asked first." });
        },
        onError: failed("The description was not saved"),
      },
    );

  return (
    <section aria-label="Tool description" className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-primary" />
          Tool description
        </h2>
        {canEdit && draft === null && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={rewrite}
              disabled={writing || noReadyFile}
              title={noReadyFile ? NO_READY_FILE : undefined}
              aria-busy={write.isPending}
            >
              {write.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              {write.isPending ? "Writing…" : kb.tool_description ? "Regenerate" : "Generate"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(kb.tool_description ?? "")} disabled={writing}>
              <Edit2 className="mr-1 h-3 w-3" />
              {kb.tool_description ? "Edit" : "Add description"}
            </Button>
          </div>
        )}
      </div>

      {canEdit && kb.tool_description_review && draft === null && (
        <div role="alert" className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <p>{REVIEW}</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={rewrite}
              disabled={writing || noReadyFile}
              title={noReadyFile ? NO_READY_FILE : undefined}
              aria-busy={write.isPending}
            >
              {write.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              {write.isPending ? "Updating…" : "Update"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={writing}
              aria-busy={keep.isPending}
              onClick={() => keep.mutate(kb.id, { onError: failed("The description was not kept") })}
            >
              {keep.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              {keep.isPending ? "Keeping…" : "Keep mine"}
            </Button>
          </div>
        </div>
      )}

      {kb.tool_description !== null && kb.tool_description_source === "generated" && longFile && (
        <p role="note" className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          {FIRST_CHARACTERS}
        </p>
      )}

      {draft !== null ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className="min-h-[200px] font-mono text-sm"
            placeholder="When should the agent search this knowledge base?"
            aria-label="Tool description"
            aria-invalid={problem ? true : undefined}
            aria-describedby="kb-description-count"
          />
          {problem && (
            <p role="alert" className="text-sm text-destructive">
              {problem}
            </p>
          )}
          <p id="kb-description-count" className="text-xs text-muted-foreground">
            {descriptionCount(draft)}
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveDraft} disabled={save.isPending || !!problem} aria-busy={save.isPending}>
              {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDraft(null)} disabled={save.isPending}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      ) : kb.tool_description ? (
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{kb.tool_description}</p>
      ) : (
        <div className="text-sm text-muted-foreground">
          <p className="font-medium">No tool description</p>
          <p className="mt-1 text-xs">It is written from your files once one is ready. You can also write one by hand.</p>
        </div>
      )}
    </section>
  );
}
